import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgModule,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { IActionBar, IActionBarFeatureSettings, IModelPageResponse } from '@ncg/data';
import { TranslateModule } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ButtonModule } from '../../../button/button.module';
import { FeatureDetectionService } from '../../../core/feature-detection.service';
import { GlobalStateService } from '../../../core/global-state.service';
import { TrackingService } from '../../../core/tracking.service';
import { UtilsModule } from '../../../utils/utils.module';
import { LazyContentData, LazyContentGenericComponent } from '../../models/lazy-content.models';
import { ActionBarTimer } from './action-bar-timer.class';
import { ActionBarService } from './action-bar.service';

@Component({
    selector: 'ncg-action-bar',
    template: `
        <div class="action-bar-backdrop" [class.action-bar-backdrop--active]="showFade"></div>
        <aside *ngIf="isEnabled" class="action-bar" [class.action-bar--centered]="isLarge">
            <ul class="action-bar__list" #ulList>
                <ng-container *ngFor="let feature of data.features; let i = index">
                    <li
                        class="action-bar__item index-{{ data.features.length - i }}"
                        [class.action-bar__item--moved-down]="!isLarge && !isExpanded"
                        [class.action-bar__item--hidden]="!isLarge && !isExpanded && expandedIndex !== i"
                        [class.action-bar__item--fill]="!isLarge"
                        [class.action-bar__item--fill-inverse]="isLarge"
                        [class.action-bar__item--inverse]="!isLarge"
                        [class.action-bar__item--hide-text]="!(hasInitialized && (expandedIndex === i || (isExpanded && !isLarge)))"
                        [class.action-bar__item--first-index]="expandedIndex === i"
                        *ngIf="feature.key !== 'Book test drive' || !disableTestDrive">
                        <button
                            type="button"
                            class="action-bar__button"
                            (click)="onBtnClick(feature)"
                            (mouseenter)="onBtnMouseEnter(i)"
                            (mouseleave)="onBtnMouseLeave(i)">
                            <span class="action-bar__label">
                                <div class="action-bar__label-text">
                                    {{ feature.label }}
                                </div>
                            </span>
                            <span class="action-bar__icon">
                                <svg-icon-sprite
                                    *ngIf="isLarge"
                                    [src]="feature.icon"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"
                                    aria-hidden="true"
                                    [attribute]="[['style', 'width:40px;height:40px;transform:scale(1);']]"></svg-icon-sprite>
                                <svg-icon-sprite
                                    *ngIf="!isLarge"
                                    [src]="feature.icon"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"
                                    aria-hidden="true"
                                    [attribute]="[['style', 'width:30px;height:30px;transform:scale(1);']]"></svg-icon-sprite>
                            </span>
                        </button>
                    </li>
                </ng-container>
                <li class="action-bar__item action-bar__item--toggle" *ngIf="!isLarge">
                    <button type="button" class="action-bar__button action-bar__button--toggle" (click)="onExpanderClick()">
                        <span class="visuallyhidden">{{ (isExpanded ? 'action_bar.close' : 'action_bar.open') | translate }}</span>
                        <span class="action-bar__icon" [class.action-bar__icon--45deg]="!isExpanded && expandedIndex === -1">
                            <svg-icon-sprite
                                [src]="'close_light'"
                                [viewBox]="'0 0 30 30'"
                                [width]="'30px'"
                                [height]="'30px'"
                                aria-hidden="true"
                                [attribute]="[['style', 'stroke:none;width:40px;height:40px;']]"></svg-icon-sprite>
                        </span>
                    </button>
                </li>
            </ul>
        </aside>
    `,
    styleUrls: ['./action-bar.component.scss'],
    providers: [ActionBarService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ActionBarComponent implements LazyContentGenericComponent<IActionBar>, AfterViewInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private readonly mqIsLarge = this.features.isBrowser() ? window.matchMedia('(min-width: 769px)') : undefined;

    private timerUntilPopoutStart: ActionBarTimer;
    private timerUntilNextPopout: ActionBarTimer;
    private timerUntilPopoutClose: ActionBarTimer;
    private timerUntilRestart: ActionBarTimer;
    private timersStarted = false;

    public isLarge = false;
    public isExpanded = false;
    public isEnabled = false;
    public hasInitialized = false;
    public expandedIndex = -1;
    public nextAutoPopoutIndex = -1;
    public showFade = false;
    public disableTestDrive = false;

    @Input()
    public data: LazyContentData<IActionBar>;

    @ViewChild('ulList')
    public ulList: ElementRef<HTMLUListElement>;

    constructor(
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly features: FeatureDetectionService,
        private readonly actionBarService: ActionBarService,
        private readonly trackingService: TrackingService,
        private readonly globalState: GlobalStateService
    ) {}

    public ngOnDestroy() {
        this.clearAll();
        this.mqIsLarge?.removeEventListener('change', this.checkIsLarge);
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public ngAfterViewInit() {
        if (this.features.isBrowser()) {
            // Window size watcher
            if (this.mqIsLarge) {
                this.isLarge = this.mqIsLarge.matches;
                this.mqIsLarge.addEventListener('change', this.checkIsLarge);
            }

            // Setup timers
            this.timerUntilPopoutStart = new ActionBarTimer(this.onPopoutStart);
            this.timerUntilNextPopout = new ActionBarTimer(this.onPopoutNext);
            this.timerUntilPopoutClose = new ActionBarTimer(this.onPopoutClose);
            this.timerUntilRestart = new ActionBarTimer(this.onPopoutRestart);

            // Delayed initialization
            setTimeout(() => {
                // Initial enabled/disabled toggle.
                this.toggleEnabled();
                this.toggleTestDrive();

                // Routing to handle page states.
                this.router.events
                    .pipe(
                        filter((e): e is NavigationStart | NavigationEnd => e instanceof NavigationStart || e instanceof NavigationEnd),
                        takeUntil(this.unsubscribe)
                    )
                    .subscribe((e) => {
                        if (e instanceof NavigationEnd) {
                            // Toggle enabled/disabled based on current page.
                            this.toggleEnabled();
                            this.toggleTestDrive();
                        } else {
                            // Add additional time to current timers.
                            this.increaseTimers();
                        }
                    });
            }, this.data.timings.loadIn);

            this.cd.markForCheck();
        }
    }

    public onBtnClick(feature: IActionBarFeatureSettings) {
        this.expandedIndex = -1;
        this.isExpanded = false;
        this.toggleFade();
        this.trackingService.actionBarConversionInProgress = undefined;

        switch (feature.key) {
            case 'Book test drive':
                this.trackingService.trackActionBarClick('test_drive');
                this.trackingService.actionBarConversionInProgress = 'test_drive';
                this.actionBarService.openInSidePanel(feature.link);
                break;
            case 'Build the car':
                this.trackingService.trackActionBarClick('build_car');
                this.actionBarService.openLink(feature.link);
                break;
            case 'Chat (Dixa)':
                this.trackingService.trackActionBarClick('chat');
                this.actionBarService.openDixa(feature.id).catch(console.error);
                break;
            case 'Chat (Weply)':
                this.trackingService.trackActionBarClick('chat');
                this.actionBarService.openWeply(feature.id).catch(console.error);
                break;
            case 'Newsletter signup':
                this.trackingService.actionBarConversionInProgress = 'newsletter';
                this.trackingService.trackActionBarClick('newsletter');
                this.actionBarService.openInSidePanel(feature.link);
                break;
            case 'Trade-in price (AutoProff)':
                this.trackingService.actionBarConversionInProgress = 'trade_in_car';
                this.trackingService.trackActionBarClick('trade_in_car');
                this.actionBarService.openAutoProff().catch(console.error);
                break;
            case 'Trade-in price (AutoUncle)':
                this.trackingService.trackActionBarClick('trade_in_car');
                this.actionBarService.openAutoUncle(feature.id).catch(console.error);
                break;
            case 'Trade-in price (Solgt.com)':
                this.trackingService.trackActionBarClick('trade_in_car');
                this.actionBarService.openSolgtCom(feature.snippet).catch(console.error);
                break;
        }
    }

    public onBtnMouseEnter(index: number) {
        this.calculateWidths();
        if (this.timersStarted) {
            this.timerUntilPopoutClose.clear();
            this.timerUntilNextPopout.clear();
        }
        this.expandedIndex = index;
    }

    public onBtnMouseLeave(index: number) {
        if (this.expandedIndex === index) {
            this.expandedIndex = -1;
        }
        if (this.timersStarted) {
            this.timerUntilNextPopout.start(this.data.timings.popout);
        }
    }

    public onExpanderClick() {
        this.isExpanded = !this.isExpanded;
        this.toggleFade();
        this.trackingService.trackActionBarExpand(this.isExpanded);
    }

    private calculateWidths() {
        setTimeout(() => {
            if (this.ulList) {
                const items: NodeListOf<HTMLLIElement> = this.ulList.nativeElement.querySelectorAll(
                    '.action-bar__item:not(.action-bar__item--toggle)'
                );
                items.forEach((e) => {
                    if (!this.isLarge) {
                        e.style.width = '100%';
                    } else {
                        const labelWidth = (e.querySelector('.action-bar__label-text') as HTMLElement).offsetWidth;
                        const iconWidth = (e.querySelector('.action-bar__icon') as HTMLElement).offsetWidth;
                        e.style.width = `${labelWidth + iconWidth}px`;
                    }
                });
            }
        });
    }

    private toggleTestDrive() {
        const pageResponse = this.globalState.getStateSynchronous('activePageResponse');
        this.disableTestDrive = Boolean(
            pageResponse &&
                pageResponse.template &&
                ['modelPage', 'modelSubpage'].includes(pageResponse.template) &&
                !(pageResponse as IModelPageResponse).allowTestDrive
        );
    }

    private toggleEnabled() {
        this.isEnabled = !(
            this.data.pages.includes(location.pathname) ||
            ['configurator', 'service-booking'].includes(this.globalState.getStateSynchronous('activeFlow'))
        );

        if (!this.isEnabled) {
            // Stop all timers.
            this.clearAll();
        } else {
            // First run setup
            if (!this.hasInitialized) {
                this.isExpanded = true;
                setTimeout(() => {
                    this.isExpanded = false;
                    this.hasInitialized = true;
                    this.toggleFade();
                    this.cd.markForCheck();
                }, this.data.timings.autoClose);
            }
            // Start timers.
            this.startTimers();
        }
        this.cd.markForCheck();
    }

    private startTimers() {
        if (this.isEnabled && this.data.timings.enabled && !this.timersStarted) {
            this.timersStarted = true;
            this.timerUntilPopoutStart.start(this.data.timings.delay);
        }
    }

    private increaseTimers = () => {
        if (this.isEnabled && this.data.timings.enabled && this.timersStarted) {
            this.timerUntilPopoutStart.increaseBy(this.data.timings.navigation);
            this.timerUntilNextPopout.increaseBy(this.data.timings.navigation);
            this.timerUntilRestart.increaseBy(this.data.timings.navigation);
        }
    };

    private onPopoutStart = () => {
        if (this.data.timings.enabled) {
            this.togglePopout(true);
            this.timerUntilNextPopout.start(this.data.timings.popout);
        }
    };

    private onPopoutNext = () => {
        if (this.data.timings.enabled) {
            this.togglePopout(true);
            if (this.nextAutoPopoutIndex !== -1) {
                this.timerUntilNextPopout.start(this.data.timings.popout);
            }
        }
    };

    private onPopoutRestart = () => {
        if (this.data.timings.enabled) {
            this.togglePopout(true);
            this.timerUntilNextPopout.start(this.data.timings.popout);
        }
    };

    private onPopoutClose = () => {
        this.togglePopout(false);
    };

    private togglePopout(open: boolean) {
        if (open) {
            this.calculateWidths();
            if (this.nextAutoPopoutIndex < this.data.features.length - 1) {
                this.nextAutoPopoutIndex = this.nextAutoPopoutIndex + 1;
            } else {
                this.nextAutoPopoutIndex = -1;
                this.timerUntilRestart.start(this.data.timings.restart);
            }
            this.expandedIndex = this.nextAutoPopoutIndex;
            this.timerUntilPopoutClose.start(this.data.timings.autoClose);
        } else {
            this.expandedIndex = -1;
        }
        this.cd.markForCheck();
    }

    private clearAll() {
        this.timerUntilPopoutStart?.clear();
        this.timerUntilNextPopout?.clear();
        this.timerUntilPopoutClose?.clear();
        this.timerUntilRestart?.clear();
        this.nextAutoPopoutIndex = -1;
        this.expandedIndex = -1;
        this.isExpanded = false;
        this.timersStarted = false;
        this.toggleFade();
    }

    private checkIsLarge = (mq: MediaQueryListEvent) => {
        const oldValue = this.isLarge;
        this.isLarge = mq.matches;
        if (oldValue !== this.isLarge) {
            this.isExpanded = false;
        }
        this.toggleFade();
        this.cd.markForCheck();
    };

    private toggleFade() {
        this.showFade = this.isExpanded && !this.isLarge;
        if (this.timersStarted) {
            if (this.isExpanded) {
                this.timerUntilPopoutClose.clear();
                this.timerUntilNextPopout.clear();
                this.expandedIndex = -1;
            } else {
                this.timerUntilNextPopout.start(this.data.timings.popout);
            }
        }
        this.cd.markForCheck();
    }
}

@NgModule({
    declarations: [ActionBarComponent],
    imports: [CommonModule, UtilsModule, ButtonModule, IconSpriteModule, TranslateModule],
})
export class ActionBarModule {}
